import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import "./DashboardPicoLog.css";

const PLOTLY_CDN = "https://cdn.plot.ly/plotly-2.35.2.min.js";

/** Couleurs légende / courbes (même ordre que backend picolog_dashboard.CURVE_COLORS) */
export const PICO_LEGEND_COLORS = [
  "#E6FF3E",
  "#72B7B2",
  "#FF9F43",
  "#FF6B9D",
  "#54A24B",
  "#A78BFA",
  "#4ECDC4",
  "#F94144",
  "#FFD93D",
  "#6C8CD5",
  "#F58518",
  "#B279A2",
  "#E45756",
  "#FF9DA6",
  "#9AE6B4",
];

function buildColorByStackId(indexEntries) {
  const map = {};
  (indexEntries || []).forEach((entry, i) => {
    map[entry.id] = PICO_LEGEND_COLORS[i % PICO_LEGEND_COLORS.length];
  });
  return map;
}

let plotlyLoader = null;
function loadPlotly() {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (plotlyLoader) return plotlyLoader;
  plotlyLoader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PLOTLY_CDN;
    s.async = true;
    s.onload = () => resolve(window.Plotly);
    s.onerror = () => reject(new Error("Impossible de charger Plotly"));
    document.head.appendChild(s);
  });
  return plotlyLoader;
}

function normalizeStackId(value) {
  let s = String(value || "").replace(/\s+/g, "").toUpperCase();
  if (!s) return "";
  if (!s.startsWith("IDS")) s = `IDS${s}`;
  return s;
}

function timeTicks(maxSeconds) {
  if (!maxSeconds || maxSeconds <= 0) return { tickvals: [0], ticktext: ["0h00"] };
  const stepsMin = [5, 10, 15, 20, 30, 60, 90, 120, 180, 240];
  const maxMin = maxSeconds / 60;
  let step = stepsMin[stepsMin.length - 1];
  for (let k = 0; k < stepsMin.length; k += 1) {
    if (maxMin / stepsMin[k] <= 8) {
      step = stepsMin[k];
      break;
    }
  }
  const stepSec = step * 60;
  const tickvals = [];
  const ticktext = [];
  for (let t = 0; t <= maxSeconds + stepSec * 0.01; t += stepSec) {
    const totalMin = Math.round(t / 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    tickvals.push(t);
    ticktext.push(`${h}h${String(m).padStart(2, "0")}`);
  }
  return { tickvals, ticktext };
}

function PicoPlot({ records, channelLabel, colorById }) {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const el = ref.current;
    if (!el || !records?.length) return undefined;

    loadPlotly()
      .then((Plotly) => {
        if (cancelled) return;
        const maxX = Math.max(...records.flatMap((r) => r.x || [0]));
        const { tickvals, ticktext } = timeTicks(maxX);

        const traces = records.map((r, i) => {
          const lineColor =
            colorById?.[r.id] ??
            PICO_LEGEND_COLORS[i % PICO_LEGEND_COLORS.length];
          return {
            x: r.x,
            y: r.y,
            type: "scatter",
            mode: "lines",
            name: r.id,
            line: { color: lineColor, width: 2.5 },
            hovertemplate: `${r.id}<br>%{x:.0f} s · %{y:.2f} °C<extra></extra>`,
          };
        });

        const legendCount = traces.length;
        const topMargin = legendCount > 4 ? 72 : legendCount > 1 ? 56 : 24;

        Plotly.react(
          el,
          traces,
          {
            margin: { t: topMargin, r: 16, b: 48, l: 52 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            font: { color: "#9a9a92", size: 11, family: "Segoe UI, system-ui, sans-serif" },
            xaxis: {
              title: { text: "Temps", font: { color: "#9a9a92", size: 12 } },
              tickfont: { color: "#b8b8b0", size: 11 },
              tickvals,
              ticktext,
              gridcolor: "rgba(255,255,255,0.06)",
              zerolinecolor: "rgba(255,255,255,0.08)",
            },
            yaxis: {
              title: {
                text: channelLabel || "°C",
                font: { color: "#9a9a92", size: 12 },
              },
              tickfont: { color: "#b8b8b0", size: 11 },
              gridcolor: "rgba(255,255,255,0.06)",
              zerolinecolor: "rgba(255,255,255,0.08)",
            },
            legend: {
              orientation: "h",
              y: 1.02,
              yanchor: "bottom",
              x: 0,
              xanchor: "left",
              bgcolor: "rgba(12, 12, 14, 0.85)",
              bordercolor: "rgba(230, 255, 62, 0.35)",
              borderwidth: 1,
              font: {
                size: 12,
                color: "#f4f4f1",
                family: "Segoe UI, system-ui, sans-serif",
              },
              itemsizing: "constant",
              tracegroupgap: 14,
            },
            showlegend: records.length > 1,
          },
          { responsive: true, displayModeBar: true, displaylogo: false }
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (window.Plotly && el) {
        window.Plotly.purge(el);
      }
    };
  }, [records, channelLabel, colorById]);

  return <div ref={ref} className="pico-chart" />;
}

export default function DashboardPicoLog() {
  const navigate = useNavigate();
  const [loadingIndex, setLoadingIndex] = useState(true);
  const [loadingCurves, setLoadingCurves] = useState(false);
  const [error, setError] = useState("");
  const [indexEntries, setIndexEntries] = useState([]);
  const [recordsById, setRecordsById] = useState({});
  const [csvDir, setCsvDir] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [stackSearch, setStackSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const recordsRef = useRef({});

  const loadIndex = useCallback(async () => {
    setLoadingIndex(true);
    setError("");
    try {
      const res = await apiFetch("/api/dashboard/picolog/index");
      const data = await res.json();
      if (!res.ok || data.status !== "success") {
        throw new Error(data.message || `Erreur HTTP ${res.status}`);
      }
      setIndexEntries(data.entries || []);
      setCsvDir(data.csvDir || "");
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoadingIndex(false);
    }
  }, []);

  useEffect(() => {
    recordsRef.current = recordsById;
  }, [recordsById]);

  const resolveEntryId = useCallback(
    (normId) => {
      const hit = indexEntries.find((e) => normalizeStackId(e.id) === normId);
      return hit?.id || normId;
    },
    [indexEntries]
  );

  const fetchCurves = useCallback(
    async (rawIds) => {
      const normIds = [...new Set(rawIds.map(normalizeStackId).filter(Boolean))];
      if (!normIds.length) return [];

      const keys = normIds.map((n) => resolveEntryId(n));
      const missing = keys.filter((k) => !recordsRef.current[k]);
      if (!missing.length) {
        return keys.map((k) => recordsRef.current[k]).filter(Boolean);
      }

      setLoadingCurves(true);
      setError("");
      try {
        const res = await apiFetch(
          `/api/dashboard/picolog/curves?ids=${encodeURIComponent(missing.join(","))}`
        );
        const data = await res.json();
        if (!res.ok || data.status !== "success") {
          throw new Error(data.message || `Erreur HTTP ${res.status}`);
        }
        const recs = data.records || [];
        setRecordsById((prev) => {
          const next = { ...prev };
          recs.forEach((r) => {
            if (r?.id) next[r.id] = r;
          });
          recordsRef.current = next;
          return next;
        });
        const merged = { ...recordsRef.current };
        recs.forEach((r) => {
          if (r?.id) merged[r.id] = r;
        });
        return keys.map((k) => merged[k]).filter(Boolean);
      } catch (e) {
        setError(e.message || "Erreur chargement courbe");
        return [];
      } finally {
        setLoadingCurves(false);
      }
    },
    [resolveEntryId]
  );

  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  const filteredEntries = useMemo(() => {
    const q = normalizeStackId(stackSearch);
    if (!q) return indexEntries;
    return indexEntries.filter((e) => {
      const id = normalizeStackId(e.id);
      return id.includes(q) || id === q;
    });
  }, [indexEntries, stackSearch]);

  const colorById = useMemo(
    () => buildColorByStackId(indexEntries),
    [indexEntries]
  );

  const records = useMemo(
    () => Object.values(recordsById),
    [recordsById]
  );

  const visible = useMemo(
    () => records.filter((r) => selected.has(r.id)),
    [records, selected]
  );

  const channelLabel = useMemo(() => {
    const labels = [...new Set(visible.map((r) => r.channel).filter(Boolean))];
    return labels.length === 1 ? labels[0] : "Température (°C)";
  }, [visible]);

  async function loadSearchStack() {
    const id = normalizeStackId(stackSearch);
    if (!id) {
      setError("Saisissez un identifiant stack (ex. IDS101660 ou 101660).");
      return;
    }
    const match = indexEntries.find((e) => normalizeStackId(e.id) === id);
    if (!match) {
      setError(`Aucun CSV PicoLog pour ${id}.`);
      return;
    }
    await fetchCurves([id]);
    setSelected(new Set([match.id]));
    setError("");
  }

  async function toggleId(id) {
    const wasSelected = selected.has(id);
    if (wasSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    if (!recordsById[id]) {
      await fetchCurves([id]);
    }
    setSelected((prev) => new Set(prev).add(id));
  }

  async function selectAllFiltered() {
    const ids = filteredEntries.map((e) => e.id);
    if (!ids.length) return;
    await fetchCurves(ids);
    setSelected(new Set(ids));
  }

  function selectNone() {
    setSelected(new Set());
  }

  async function exportExcel() {
    if (!visible.length) return;
    setExporting(true);
    setExportError("");
    try {
      const ids = visible.map((r) => r.id).join(",");
      const res = await apiFetch(
        `/api/dashboard/picolog/export-excel?ids=${encodeURIComponent(ids)}`
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Erreur HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `picolog_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || "Export Excel impossible");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="pico-page">
      <MenuBar active="dashboard" />
      <div className="pico-wrap">
        <header className="pico-hero">
          <div>
            <p className="pico-kicker">Dashboard · Production</p>
            <h1 className="pico-title">PicoLog — courbes température</h1>
          </div>
          <button type="button" className="pico-back" onClick={() => navigate(-1)}>
            Retour
          </button>
        </header>

        <div className="pico-search-bar">
          <label className="pico-search-label" htmlFor="pico-stack-search">
            Recherche stack
          </label>
          <div className="pico-search-row">
            <input
              id="pico-stack-search"
              type="search"
              className="pico-search-input"
              placeholder="IDS101660 ou 101660"
              value={stackSearch}
              onChange={(e) => setStackSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") loadSearchStack();
              }}
            />
            <button
              type="button"
              className="pico-btn-load"
              disabled={loadingCurves}
              onClick={loadSearchStack}
            >
              {loadingCurves ? "Chargement…" : "Charger la courbe"}
            </button>
          </div>
        </div>

        {loadingIndex && (
          <div className="pico-loading">Index des fichiers PicoLog…</div>
        )}
        {error && !loadingIndex && <div className="pico-error">{error}</div>}

        {!loadingIndex && !error && indexEntries.length === 0 && (
          <div className="pico-empty">
            Aucun fichier CSV dans le dossier picolog.
            {csvDir ? ` (${csvDir})` : ""}
          </div>
        )}

        {!loadingIndex && indexEntries.length > 0 && (
          <div className="pico-layout">
            <aside className="pico-sidebar">
              <p className="pico-sidebar-hd">
                Fichiers ({filteredEntries.length}
                {stackSearch ? ` / ${indexEntries.length}` : ""})
              </p>
              <div className="pico-actions">
                <button type="button" className="pico-btn-sm" onClick={selectAllFiltered}>
                  Tous (filtre)
                </button>
                <button type="button" className="pico-btn-sm" onClick={selectNone}>
                  Aucun
                </button>
                <button type="button" className="pico-btn-sm" onClick={loadIndex}>
                  Index
                </button>
                <button
                  type="button"
                  className="pico-btn-sm pico-btn-excel"
                  disabled={exporting || visible.length === 0}
                  onClick={exportExcel}
                >
                  {exporting ? "Export…" : "Export Excel"}
                </button>
              </div>
              {exportError && <p className="pico-export-err">{exportError}</p>}
              {loadingCurves && (
                <p className="pico-loading-inline">Lecture CSV en cours…</p>
              )}
              <ul className="pico-list">
                {filteredEntries.map((entry) => {
                  const loaded = recordsById[entry.id];
                  const color =
                    colorById[entry.id] || "rgba(114,183,178,0.35)";
                  return (
                    <li key={entry.id}>
                      <label className="pico-item">
                        <span className="pico-dot" style={{ background: color }} />
                        <input
                          type="checkbox"
                          checked={selected.has(entry.id)}
                          onChange={() => toggleId(entry.id)}
                        />
                        <span>
                          <span className="pico-item-id">{entry.id}</span>
                          <span className="pico-item-meta">
                            {loaded
                              ? `${loaded.durationTxt} · ${loaded.pointCount} pts · ${loaded.yMin}–${loaded.yMax} °C`
                              : `${entry.modifiedAt} · non chargé`}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <div className="pico-main">
              <div className="pico-channel">{channelLabel}</div>
              {visible.length === 0 ? (
                <div className="pico-empty">
                  Recherchez un stack et cliquez « Charger la courbe », ou cochez un fichier dans la
                  liste.
                </div>
              ) : (
                <>
                  <PicoPlot
                    records={visible}
                    channelLabel={channelLabel}
                    colorById={colorById}
                  />
                  <p className="pico-hint">
                    Molette = zoom · glisser = sélection · double-clic = reset · {visible.length}{" "}
                    courbe(s) affichée(s)
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
